import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { ChatOverlayProvider } from './contexts/ChatOverlayContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { PartnerProtectedRoute } from './components/PartnerProtectedRoute';
// Perf fix (Sep 2026): these are always-mounted, route-independent utility
// components (native/PWA glue, probes, banners). None are needed for the
// initial paint, so they are code-split out of the eager entry bundle via
// React.lazy and mounted inside <Suspense fallback={null}> below — they
// were previously imported eagerly here, adding parse/exec weight to TBT
// before the app became interactive. Behavior is unchanged: each still
// mounts unconditionally, just on the next tick after its chunk resolves.
const AndroidBackHandler = lazy(() => import('./components/AndroidBackHandler'));
const ShareDestinationModal = lazy(() => import('./components/ShareDestinationModal'));
const PWAInstallPrompt = lazy(() => import('./components/PWAInstallPrompt').then(m => ({ default: m.PWAInstallPrompt })));
const AppInstallBanner = lazy(() => import('./components/AppInstallBanner'));
const PlatformProbe = lazy(() => import('./components/PlatformProbe'));
const ThermalProbeBridge = lazy(() => import('./components/VoiceRoom/ThermalProbeBridge'));
const RecordingUploadResumer = lazy(() => import('./components/RecordingUploadResumer'));
const UpdateRequiredModal = lazy(() => import('./components/UpdateRequiredModal').then(m => ({ default: m.UpdateRequiredModal })));
const OfflineBanner = lazy(() => import('./components/OfflineBanner').then(m => ({ default: m.OfflineBanner })));
import { Toaster } from './components/ui/sonner';
import { LiveEmergencyForegroundAlert } from './components/LiveEmergencyForegroundAlert';
import { initGA } from './lib/ga';
import { startVersionPolling } from './lib/versionCheck';
import { isPwaOrWeb } from './lib/platform';
// Mobile-perf fix (Feb 2026): only `Login` is eager-imported. Every other
// page is code-split via `React.lazy` so the bundle the device parses on the
// /login route drops from ≈21 MB heap to ≈6 MB → eliminates the
// first-keystroke GC pause measured on mid-tier Android (was 1.2 s).
import { Login } from './pages/Login';
import { UnifiedLoadingState } from './components/ui-system';
// Notification Pipeline V2 · M3 — Deep-link resolver route.
const DeepLinkResolver = lazy(() => import('./components/DeepLinkResolver'));
const ShareReceive     = lazy(() => import('./pages/ShareReceive'));
const StudentAssignmentsHub = lazy(() => import('./pages/StudentAssignmentsHub'));
const ClassRecordings = lazy(() => import('./pages/ClassRecordings'));
const RecordingsAdmin = lazy(() => import('./pages/admin/RecordingsAdmin'));
const CertificatesAdmin = lazy(() => import('./pages/admin/CertificatesAdmin'));
const MyCertificates = lazy(() => import('./pages/MyCertificates'));
const VerifyCertificate = lazy(() => import('./pages/VerifyCertificate'));
const FamilyCertificates = lazy(() => import('./pages/FamilyCertificates'));
const Dashboard = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })));
const PublicRegistration = lazy(() => import('./pages/PublicRegistration').then(m => ({ default: m.PublicRegistration })));
const FreeCourseLandingPage = lazy(() => import('./pages/FreeCourseLandingPage'));
const LegacyStudentRegistration = lazy(() => import('./pages/LegacyStudentRegistration').then(m => ({ default: m.LegacyStudentRegistration })));
// Sprint 4 (Feb 2026) — legacy Pre-Class Lobby lazy import removed
// with the retirement of the reminder countdown workflow. The file
// `./pages/PreClassLobby.jsx` is preserved on disk for reference.
// PoC (Feb 2026) — Self-hosted LiveKit voice room. Single classroom,
// feature-flagged via classrooms.voice_provider == 'livekit'. Zero impact
// on the existing Zoom flow.
const VoiceRoom = lazy(() => import('./pages/VoiceRoom'));
const StudentReferrals = lazy(() => import('./pages/StudentReferrals'));
const AdminReferrals = lazy(() => import('./pages/AdminReferrals'));
const PartnerLogin = lazy(() => import('./pages/PartnerLogin'));
const PartnerDashboard = lazy(() => import('./pages/PartnerDashboard'));
const PartnerChangePassword = lazy(() => import('./pages/PartnerChangePassword'));
// Phase B.2 (Feb 2026) — Browser Handoff page for Android screen share.
// Public route (no session cookie required) — the URL-supplied handoff
// token IS the auth artefact and is verified by the backend at exchange.
const ScreenShareHandoff = lazy(() => import('./pages/ScreenShareHandoff'));
const LegacyRegistrations = lazy(() => import('./pages/LegacyRegistrations').then(m => ({ default: m.LegacyRegistrations })));
const RegistrationManagement = lazy(() => import('./pages/RegistrationManagement').then(m => ({ default: m.RegistrationManagement })));
const StudentsManagement = lazy(() => import('./pages/StudentsManagement').then(m => ({ default: m.StudentsManagement })));
const ClassroomsManagement = lazy(() => import('./pages/ClassroomsManagement').then(m => ({ default: m.ClassroomsManagement })));
const FinanceManagement = lazy(() => import('./pages/FinanceManagement').then(m => ({ default: m.FinanceManagement })));
const UsersManagement = lazy(() => import('./pages/UsersManagement').then(m => ({ default: m.UsersManagement })));
const ParentRelations = lazy(() => import('./pages/admin/ParentRelations'));
const FamilyLinkAccept = lazy(() => import('./pages/FamilyLinkAccept'));
const AcademicStructure = lazy(() => import('./pages/AcademicStructure').then(m => ({ default: m.AcademicStructure })));
const ContentLibraryCms = lazy(() => import('./pages/ContentLibraryCms'));
const PlacementManagement = lazy(() => import('./pages/PlacementManagement').then(m => ({ default: m.PlacementManagement })));
const AttendanceManagement = lazy(() => import('./pages/AttendanceManagement').then(m => ({ default: m.AttendanceManagement })));
const MyExcuses = lazy(() => import('./pages/MyExcuses').then(m => ({ default: m.MyExcuses })));
const ReliabilityReport = lazy(() => import('./pages/ReliabilityReport').then(m => ({ default: m.ReliabilityReport })));
const NetworkStatsAdmin = lazy(() => import('./pages/NetworkStatsAdmin'));
const ClassroomGroups = lazy(() => import('./pages/ClassroomGroups').then(m => ({ default: m.ClassroomGroups })));
const WorkQueues = lazy(() => import('./pages/WorkQueues').then(m => ({ default: m.WorkQueues })));
const OperationsDashboard = lazy(() => import('./pages/OperationsDashboard').then(m => ({ default: m.OperationsDashboard })));
const AcademicManagement = lazy(() => import('./pages/AcademicManagement').then(m => ({ default: m.AcademicManagement })));
const DataImport = lazy(() => import('./pages/DataImport').then(m => ({ default: m.DataImport })));
const ScheduleExceptions = lazy(() => import('./pages/ScheduleExceptions').then(m => ({ default: m.ScheduleExceptions })));
const Subscriptions = lazy(() => import('./pages/Subscriptions').then(m => ({ default: m.Subscriptions })));
const Trials = lazy(() => import('./pages/Trials').then(m => ({ default: m.Trials })));
const TeacherApply = lazy(() => import('./pages/TeacherApply'));
const TeacherApplicationsAdmin = lazy(() => import('./pages/admin/TeacherApplicationsAdmin'));
const LessonFeedbackAdmin = lazy(() => import('./pages/admin/LessonFeedbackAdmin'));
const Assignments = lazy(() => import('./pages/Assignments').then(m => ({ default: m.Assignments })));
const AssessmentsCms = lazy(() => import('./pages/AssessmentsCms').then(m => ({ default: m.AssessmentsCms })));
const RechargeCardsAdmin = lazy(() => import('./pages/admin/RechargeCardsAdmin'));
const AmbiguousSessions = lazy(() => import('./pages/admin/AmbiguousSessions'));
const AudioHealth = lazy(() => import('./pages/admin/AudioHealth'));
const TeacherContributionsReview = lazy(() => import('./pages/TeacherContributionsReview'));
const DuesReviewRequests = lazy(() => import('./pages/admin/DuesReviewRequests'));
const TeacherDuesLogPage = lazy(() => import('./pages/TeacherDuesLogPage'));
const CardStore = lazy(() => import('./pages/CardStore'));
const AssessmentMonitor = lazy(() => import('./pages/AssessmentMonitor').then(m => ({ default: m.AssessmentMonitor })));
const AssessmentAnalytics = lazy(() => import('./pages/AssessmentAnalytics').then(m => ({ default: m.AssessmentAnalytics })));
const GradingQueuePage = lazy(() => import('./pages/GradingQueuePage').then(m => ({ default: m.GradingQueuePage })));
const StudentAssessments = lazy(() => import('./pages/StudentAssessments').then(m => ({ default: m.StudentAssessments })));
const CompetitionHub = lazy(() => import('./pages/CompetitionHub'));
const CompetitionJudgeConsole = lazy(() => import('./pages/CompetitionJudgeConsole'));
const CompetitionsAdmin = lazy(() => import('./pages/admin/CompetitionsAdmin'));
const CompetitionStudio = lazy(() => import('./pages/CompetitionStudio'));
const AssessmentRunner = lazy(() => import('./pages/AssessmentRunner').then(m => ({ default: m.AssessmentRunner })));
const Reports = lazy(() => import('./pages/Reports').then(m => ({ default: m.Reports })));
const ZoomUsageReport = lazy(() => import('./pages/ZoomUsageReport'));
const ZoomAccessRequests = lazy(() => import('./pages/ZoomAccessRequests'));
const PlacementTest = lazy(() => import('./pages/PlacementTest').then(m => ({ default: m.PlacementTest })));
const PlacementTestResults = lazy(() => import('./pages/PlacementTestResults').then(m => ({ default: m.PlacementTestResults })));
const Distribution = lazy(() => import('./pages/Distribution').then(m => ({ default: m.Distribution })));
const PricingManagement = lazy(() => import('./pages/PricingManagement').then(m => ({ default: m.PricingManagement })));
const ChangePassword = lazy(() => import('./pages/ChangePassword').then(m => ({ default: m.ChangePassword })));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword').then(m => ({ default: m.ForgotPassword })));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const PasswordResetRequests = lazy(() => import('./pages/PasswordResetRequests').then(m => ({ default: m.PasswordResetRequests })));
const Settings = lazy(() => import('./pages/Settings').then(m => ({ default: m.Settings })));
const MySupportRequests = lazy(() => import('./pages/MySupportRequests').then(m => ({ default: m.MySupportRequests })));
const AdminNotificationsManagement = lazy(() => import('./pages/AdminNotificationsManagement'));
const SupportInbox = lazy(() => import('./pages/SupportInbox').then(m => ({ default: m.SupportInbox })));
const Unauthorized = lazy(() => import('./pages/Unauthorized').then(m => ({ default: m.Unauthorized })));
const ParentModuleDisabled = lazy(() => import('./pages/ParentModuleDisabled').then(m => ({ default: m.ParentModuleDisabled })));
const Privacy = lazy(() => import('./pages/Privacy').then(m => ({ default: m.Privacy })));
const Terms = lazy(() => import('./pages/Terms').then(m => ({ default: m.Terms })));
const TwoFactorVerify = lazy(() => import('./pages/TwoFactorVerify').then(m => ({ default: m.TwoFactorVerify })));
const AuditLogs = lazy(() => import('./pages/AuditLogs').then(m => ({ default: m.AuditLogs })));
const TeacherGuide = lazy(() => import('./pages/TeacherGuide'));
const StudentGuide = lazy(() => import('./pages/StudentGuide'));
const Leaderboard = lazy(() => import('./pages/Leaderboard'));
const MespCore = lazy(() => import('./pages/MespCore'));
const HealthMonitor = lazy(() => import('./pages/admin/HealthMonitor'));
const AcademicAssistant = lazy(() => import('./pages/admin/AcademicAssistant'));
const PlatformAdoption = lazy(() => import('./pages/admin/PlatformAdoption'));
const TeacherBadgesPage = lazy(() => import('./pages/TeacherBadgesPage').then(m => ({ default: m.TeacherBadgesPage })));
const AdminShiftCoveragePage = lazy(() => import('./pages/AdminShiftCoveragePage').then(m => ({ default: m.AdminShiftCoveragePage })));
const TeacherDuesPage = lazy(() => import('./pages/TeacherDuesPage'));
const ClassroomMonitoringPage = lazy(() => import('./pages/ClassroomMonitoringPage'));
const DuplicateProfilesPage = lazy(() => import('./pages/DuplicateProfilesPage'));
const AnnouncementManager = lazy(() => import('./pages/admin/AnnouncementManager'));
const UnifiedCommunications = lazy(() => import('./pages/admin/UnifiedCommunications'));
const WhatsAppCommunityManager = lazy(() => import('./pages/admin/WhatsAppCommunityManager'));
const PagingMonitor = lazy(() => import('./pages/admin/PagingMonitor'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const DeleteAccount = lazy(() => import('./pages/DeleteAccount'));
const CommunicationsCenter = lazy(() => import('./pages/CommunicationsCenter'));
const TeacherShiftsPage = lazy(() => import('./pages/TeacherShiftsPage'));
const TeacherLeavePage = lazy(() => import('./pages/TeacherLeavePage'));
const EnrichmentHub = lazy(() => import('./pages/EnrichmentHub'));
const EnrichmentMonitor = lazy(() => import('./pages/admin/EnrichmentMonitor'));
const LessonArchitect = lazy(() => import('./pages/LessonArchitect'));
const LessonPrepMonitor = lazy(() => import('./pages/admin/LessonPrepMonitor'));
const AcademicProgressTracker = lazy(() => import('./pages/admin/AcademicProgressTracker'));
const BadgesPage = lazy(() => import('./pages/BadgesPage'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));
const RegistrationFlow = lazy(() => import('./pages/RegistrationFlow'));
const StudentOnboarding = lazy(() => import('./pages/StudentOnboarding').then(m => ({ default: m.StudentOnboarding })));
// Phase 40.6 — Onboarding visual-identity preview (public, no auth).
const OnboardingMockup = lazy(() => import('./pages/OnboardingMockup'));
// Feb 2026 — Public welcome landing page (redesigned entry point).
const WelcomeLanding = lazy(() => import('./pages/WelcomeLanding'));
// Internal preview route for the Unified Design System (Phase 1.A).
// Public/hidden — used by the team to visually verify components before
// rolling them out across the platform.
const DesignSystemShowcase = lazy(() => import('./pages/DesignSystemShowcase'));
// Jun 2026 — Read-only duplicates report viewer (Super_Admin).
const DuplicatesReport = lazy(() => import('./pages/DuplicatesReport'));
const LiftRestrictions = lazy(() => import('./pages/LiftRestrictions'));
// Feb 2026 — Cross-env library migration tool (Super_Admin).
const LibraryMigration = lazy(() => import('./pages/LibraryMigration'));
const RegistrationCases = lazy(() => import('./pages/RegistrationCases'));
// TEMPORARY (Jun 25, 2026) — RailPreview, public route for screenshotting
// the whiteboard size sub-rail without entering a voice session.
const RailPreview = lazy(() => import('./pages/_RailPreview'));

// Phase 9 (Feb 2026, UX overhaul) — Admin Hub pages that aggregate
// existing screens into a single, tabbed surface. The old standalone
// routes still work and are linked to from the Hub tab strip.
const InboxHub      = lazy(() => import('./pages/admin/InboxHub'));
const OperationsHub = lazy(() => import('./pages/admin/OperationsHub'));
const DataQualityConsole = lazy(() => import('./pages/admin/DataQualityConsole'));
const AcademicHub   = lazy(() => import('./pages/admin/AcademicHub'));
const MatchReview   = lazy(() => import('./pages/admin/MatchReview'));
const PlacementPreview = lazy(() => import('./pages/admin/PlacementPreview'));
const PlacementV3Settings = lazy(() => import('./pages/admin/PlacementV3Settings'));
const PlacementV3PendingMedia = lazy(() => import('./pages/admin/PlacementV3PendingMedia'));
const NavigationEditor = lazy(() => import('./pages/admin/NavigationEditor'));
const PlacementV3ThemePreview = lazy(() => import('./pages/placement/PlacementV3ThemePreview'));
const FinanceHub    = lazy(() => import('./pages/admin/FinanceHub'));
const ReportsHub    = lazy(() => import('./pages/admin/ReportsHub'));

import { isParentModuleEnabled } from './lib/featureFlags';
import './index.css';

// Lightweight fallback shown while a code-split chunk is being fetched.
// Feb 2026 (Wave 11 · Product Polish) — routed through the platform-wide
// `UnifiedLoadingState` so every "fetching your next screen" moment
// matches the loaders inside pages (student dashboard, DIOC, assignments,
// reports, etc.). One call site → one visual language across all 40+
// lazy-loaded routes.
const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#F5F5F0]" data-testid="route-fallback">
    <UnifiedLoadingState variant="inline" size="lg" />
  </div>
);

// Feb 2026 — public root route. Authenticated users go straight to
// `/dashboard`; everyone else lands on the redesigned `WelcomeLanding`
// (two CTAs + Google). Reuses the existing `useAuth` context, no new
// state/store.
const RootRoute = () => {
  const { user, loading } = useAuth();
  if (loading) return <RouteFallback />;
  if (user) return <Navigate to="/dashboard" replace />;
  return <WelcomeLanding />;
};

function App() {
  // Register the Service Worker on mount so the PWA is installable and
  // ready to receive Web Push events. Subscription only happens after the
  // user clicks the "Enable Notifications" button (see NotificationsBell).
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch(() => {});
    }
    initGA();
  }, []);

  // Block Chrome/Google Translate from rewriting the platform UI.
  // The <html translate="no" class="notranslate"> attribute set in
  // public/index.html cascades to every descendant, but we re-assert
  // it on <body> here at runtime so any third-party script that
  // mutates the DOM (PostHog, browser extensions, etc.) cannot regress
  // the protection. Idempotent — safe to run on every render.
  useEffect(() => {
    try {
      document.documentElement.setAttribute('translate', 'no');
      document.documentElement.classList.add('notranslate');
      document.body.setAttribute('translate', 'no');
      document.body.classList.add('notranslate');
    } catch (_) { /* SSR / non-DOM env */ }
  }, []);

  // Stale-bundle detector. Polls /api/version every 60s and on
  // visibilitychange→visible. When a new server_version or frontend
  // bundle hash is detected, <UpdateRequiredModal> takes over the UI
  // and forces the user to reload. Root-cause fix for the Jun 23, 2026
  // classroom 318 / 639 incidents (teacher & student stuck on cached
  // pre-deploy code, missing Whiteboard fixes entirely).
  useEffect(() => {
    const stop = startVersionPolling();
    return stop;
  }, []);

  // Notification Pipeline V2 · Milestone 3 (Feb 2026) — Wrapper bootstrap.
  // No-op on the web (isNative() short-circuits every call). Inside a
  // Capacitor build this wires up:
  //   • FCM registration + token upload to /api/device-tokens/register
  //   • App.addListener('appUrlOpen') → routes /dl/<nonce> into React Router
  useEffect(() => {
    let unsubscribe = () => {};
    (async () => {
      try {
        const bridge = await import('./lib/mobileBridge');
        // History is not yet mounted here (we are outside BrowserRouter);
        // use window.location.replace as a portable fallback for the
        // deep-link callback.
        const nav = (path) => { try { window.location.replace(path); } catch (_) {} };
        const result = await bridge.bootstrapMobileIntegration({ navigate: nav });
        if (result?.unsubscribe) unsubscribe = result.unsubscribe;
      } catch (_) { /* web bundle — bridge absent is fine */ }
    })();
    return () => { try { unsubscribe(); } catch (_) {} };
  }, []);

  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <ChatOverlayProvider>
          {/* Perf fix (Sep 2026): these always-mounted utility components are
              now React.lazy (see imports above) — Suspense fallback={null}
              means nothing renders in their place while the chunk loads, so
              there is no layout shift; each pops in a moment after first
              paint instead of blocking it. */}
          <Suspense fallback={null}>
            {/* Jun 2026 — Android hardware back-button controller (native only). */}
            <AndroidBackHandler />
            {/* Jun 2026 — Universal Share Handler: global overlay for the
                Android share sheet (role-based destination selector). */}
            <ShareDestinationModal />
            <UpdateRequiredModal />
            <OfflineBanner />
            {/* Jun 2026 — Android users get the Play-Store banner instead of
                the PWA prompt (native app supersedes PWA on Android). */}
            {isPwaOrWeb() && !/android/i.test(navigator.userAgent || '') && <PWAInstallPrompt />}
            <AppInstallBanner />
            <PlatformProbe />
            <ThermalProbeBridge />
            <RecordingUploadResumer />
          </Suspense>
          {/* Jul 2026 — single global MESP-branded Toaster (navy/gold,
              top-center). Every toast.* call across all roles renders
              through this one mount for consistent styling. */}
          <Toaster />
          <LiveEmergencyForegroundAlert />
          <Suspense fallback={<RouteFallback />}>
            <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/partner/login" element={<PartnerLogin />} />
            <Route path="/partner/change-password" element={<PartnerProtectedRoute allowPasswordChange><PartnerChangePassword /></PartnerProtectedRoute>} />
            <Route path="/partner" element={<PartnerProtectedRoute><PartnerDashboard /></PartnerProtectedRoute>} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/join-teachers/:token" element={<TeacherApply />} />
            <Route path="/delete-account" element={<DeleteAccount />} />
            {/* Public route — TEMPORARY for screenshotting the rail. */}
            <Route path="/__rail-preview" element={<RailPreview />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/auth/callback/native/:handoffId" element={<AuthCallback />} />
            <Route path="/two-factor" element={<TwoFactorVerify />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/register" element={<PublicRegistration />} />
            <Route path="/free/:code" element={<FreeCourseLandingPage />} />
            <Route path="/legacy-student" element={<LegacyStudentRegistration />} />
            {/* Phase 40.6 — Onboarding visual-identity preview. Public,
                no auth, NOT linked from main nav. For user approval only. */}
            <Route path="/onboarding-mockup" element={<OnboardingMockup />} />
            {/* Phase 1.A — Unified Design System preview. Public, no auth,
                hidden from navigation. For visual verification only. */}
            <Route path="/design-system" element={<DesignSystemShowcase />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/unauthorized" element={<Unauthorized />} />
            {/* Notification Pipeline V2 · M3 — deep-link resolver (Feb 2026) */}
            <Route path="/dl/:nonce" element={<DeepLinkResolver />} />
            <Route path="/share-receive" element={<ShareReceive />} />

            {/* Phase B.2 (Feb 2026) — Android screen-share Browser Handoff.
                Public route. The `?t=<handoff-jwt>` query parameter is
                the auth; validated server-side at /exchange. Never
                wrap in ProtectedRoute — the app opens this in an
                external browser without the session cookie. */}
            <Route
              path="/screen-share-handoff/:classroomId"
              element={<ScreenShareHandoff />}
            />

            {/* Student onboarding gate (Feb 2026). Authenticated Students
                whose profile/review/distribution is incomplete land here.
                ProtectedRoute enforces the redirect — this route only
                renders the waiting/profile-required screen. */}
            <Route
              path="/student-onboarding"
              element={
                <ProtectedRoute>
                  <StudentOnboarding />
                </ProtectedRoute>
              }
            />
            
            {/* Protected Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />

            {/* Sprint 4 (Feb 2026) — Legacy Pre-Class Lobby retired.
                Reminder V2 (native Android FCM) opens /voice-room/{cid}
                directly. Route + component kept off the router but the
                file is preserved on disk for historical reference and
                for stale clients still holding the old bundle in cache. */}

            {/* PoC (Feb 2026) — LiveKit Self-Hosted Voice Room.
                Full-screen, single classroom, feature-flagged. Only
                renders for a classroom whose voice_provider == 'livekit'. */}
            <Route
              path="/voice-room/:classroomId"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Academic_Supervisor', 'Teacher', 'Student']}>
                  <VoiceRoom />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/registration"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Registration_Officer', 'Academic_Supervisor']} permission="can_access_registration">
                  <RegistrationManagement />
                </ProtectedRoute>
              }
            />

            <Route
              path="/legacy-registrations"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Academic_Supervisor']} permission="can_access_registration">
                  <LegacyRegistrations />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/paging-monitor"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Academic_Supervisor', 'Admin']}>
                  <PagingMonitor />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/whatsapp-communities"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Academic_Supervisor']}>
                  <WhatsAppCommunityManager />
                </ProtectedRoute>
              }
            />
            {/* Referral Growth Engine (Jun 2026) */}
            <Route
              path="/referrals"
              element={
                <ProtectedRoute allowedRoles={['Student']}>
                  <StudentReferrals />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/referrals"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Financial_Manager', 'Academic_Supervisor', 'Registration_Officer']}>
                  <AdminReferrals />
                </ProtectedRoute>
              }
            />

            {/* Jun 2026 — Recharge Card Vault (web-only feature; pages self-hide on Android) */}
            <Route
              path="/admin/recharge-cards"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Financial_Manager']}>
                  <RechargeCardsAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/ambiguous-sessions"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin']}>
                  <AmbiguousSessions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/audio-health"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin']}>
                  <AudioHealth />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/question-contributions"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Academic_Supervisor']}>
                  <TeacherContributionsReview />
                </ProtectedRoute>
              }
            />
            <Route
              path="/card-store"
              element={
                <ProtectedRoute allowedRoles={['Student']}>
                  <CardStore />
                </ProtectedRoute>
              }
            />

            <Route
              path="/password-reset-requests"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Academic_Supervisor']} permission="can_manage_users">
                  <PasswordResetRequests />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/students"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Academic_Supervisor', 'Registration_Officer']} permission="can_access_students">
                  <StudentsManagement />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/classrooms"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Academic_Supervisor', 'Teacher']} permission="can_access_classrooms">
                  <ClassroomsManagement />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/finance"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Financial_Manager', 'Academic_Supervisor']} permission="can_access_finance">
                  <FinanceManagement />
                </ProtectedRoute>
              }
            />

            <Route
              path="/pricing"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Financial_Manager']} permission="can_access_finance">
                  <PricingManagement />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/parent-relations"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Academic_Supervisor', 'Registration_Officer']}>
                  <ParentRelations />
                </ProtectedRoute>
              }
            />

            <Route
              path="/users"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Academic_Supervisor']} permission="can_manage_users">
                  <UsersManagement />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin-notifications"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Academic_Supervisor', 'Admin', 'Supervisor']}>
                  <AdminNotificationsManagement />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/communications"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Academic_Supervisor', 'Admin', 'Supervisor']}>
                  <UnifiedCommunications />
                </ProtectedRoute>
              }
            />

            <Route
              path="/communications-center"
              element={
                <ProtectedRoute allowedRoles={['Student', 'Teacher', 'Parent']}>
                  <CommunicationsCenter />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/duplicates"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Academic_Supervisor']}>
                  <DuplicatesReport />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/lift-restrictions"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin']}>
                  <LiftRestrictions />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/library-migration"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin']}>
                  <LibraryMigration />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/registration-cases"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin']}>
                  <RegistrationCases />
                </ProtectedRoute>
              }
            />

            <Route
              path="/academic-structure"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Academic_Supervisor']} permission="can_manage_academic_structure">
                  <AcademicStructure />
                </ProtectedRoute>
              }
            />

            <Route
              path="/content-library"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Academic_Supervisor']} permission="can_manage_academic_structure">
                  <ContentLibraryCms />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/placement"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Academic_Supervisor']} permission="can_access_distribution">
                  <PlacementManagement />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/academic"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Academic_Supervisor']} permission="can_manage_academic_structure">
                  <AcademicManagement />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/attendance"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Teacher', 'Academic_Supervisor']} permission="can_access_classrooms">
                  <AttendanceManagement />
                </ProtectedRoute>
              }
            />

            <Route
              path="/my-assignments"
              element={
                <ProtectedRoute allowedRoles={['Student']}>
                  <StudentAssignmentsHub />
                </ProtectedRoute>
              }
            />

            <Route
              path="/class-recordings"
              element={
                <ProtectedRoute allowedRoles={['Student', 'Teacher', 'Super_Admin', 'Platform_Administration', 'Academic_Supervisor']}>
                  <ClassRecordings />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/class-recordings"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Platform_Administration', 'Academic_Supervisor']}>
                  <RecordingsAdmin />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/certificates"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Platform_Administration', 'Academic_Supervisor']}>
                  <CertificatesAdmin />
                </ProtectedRoute>
              }
            />

            <Route
              path="/my-certificates"
              element={
                <ProtectedRoute allowedRoles={['Student']}>
                  <MyCertificates />
                </ProtectedRoute>
              }
            />

            <Route path="/verify/:serial" element={<VerifyCertificate />} />

            <Route
              path="/family-certificates"
              element={
                <ProtectedRoute allowedRoles={['Parent']}>
                  <FamilyCertificates />
                </ProtectedRoute>
              }
            />

            <Route
              path="/my-excuses"
              element={
                <ProtectedRoute allowedRoles={['Student', 'Parent']}>
                  <MyExcuses />
                </ProtectedRoute>
              }
            />

            <Route
              path="/reliability-report"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Academic_Supervisor']}>
                  <ReliabilityReport />
                </ProtectedRoute>
              }
            />

            <Route
              path="/network-stats"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Academic_Supervisor']}>
                  <NetworkStatsAdmin />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/classroom-groups"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Academic_Supervisor', 'Teacher', 'Student']} permission="can_access_class_groups">
                  <ClassroomGroups />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/work-queues"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Registration_Officer', 'Academic_Supervisor', 'Financial_Manager']} permission="can_access_students">
                  <WorkQueues />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/operations"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Registration_Officer', 'Academic_Supervisor', 'Financial_Manager']} permission="can_access_students">
                  <OperationsDashboard />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/data-import"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Registration_Officer', 'Academic_Supervisor']} permission="can_access_students">
                  <DataImport />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/schedule-exceptions"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Academic_Supervisor']} permission="can_access_schedule_exceptions">
                  <ScheduleExceptions />
                </ProtectedRoute>
              }
            />
            
            {/* Jun 2026 — أبنائي merged into the parent dashboard */}
            <Route path="/my-children" element={<Navigate to="/dashboard" replace />} />

            <Route path="/parent-disabled" element={<ParentModuleDisabled />} />

            <Route
              path="/family-link/:token"
              element={
                <ProtectedRoute allowedRoles={['Student']}>
                  <FamilyLinkAccept />
                </ProtectedRoute>
              }
            />

            <Route
              path="/support/my-requests"
              element={
                <ProtectedRoute allowedRoles={['Student', 'Parent', 'Teacher']}>
                  <MySupportRequests />
                </ProtectedRoute>
              }
            />

            <Route
              path="/support/inbox"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Academic_Supervisor']} permission="can_manage_users">
                  <SupportInbox />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/subscriptions"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Academic_Supervisor', 'Financial_Manager']} permission="can_access_finance">
                  <Subscriptions />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/admin/teacher-applications"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Admin', 'Academic_Supervisor']}>
                  <TeacherApplicationsAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/lesson-feedback"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Academic_Supervisor']}>
                  <LessonFeedbackAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/trials"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Academic_Supervisor', 'Registration_Officer']} permission="can_access_trials">
                  <Trials />
                </ProtectedRoute>
              }
            />

            <Route
              path="/assignments"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Academic_Supervisor', 'Teacher', 'Student', 'Parent']} permission="can_access_assignments">
                  <Assignments />
                </ProtectedRoute>
              }
            />

            {/* Assessment System — Phase P2 (CMS Builder, Feb 2026).
                Teachers/Admins build and manage tests here. The Student
                Runner (P3) is a separate route that will be added later. */}
            <Route
              path="/assessments"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Academic_Supervisor', 'Teacher']}>
                  <AssessmentsCms />
                </ProtectedRoute>
              }
            />

            {/* Phase P4 — Teacher Live Monitor + Post-hoc Analytics.
                Both surfaces are staff-only (Student attempts are
                filtered by the endpoint too). */}
            <Route
              path="/assessments/:assessmentId/monitor"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Academic_Supervisor', 'Teacher']}>
                  <AssessmentMonitor />
                </ProtectedRoute>
              }
            />
            <Route
              path="/assessments/:assessmentId/analytics"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Academic_Supervisor', 'Teacher']}>
                  <AssessmentAnalytics />
                </ProtectedRoute>
              }
            />
            {/* Sprint C (Jun 2026) — Unified Grading Queue («طابور
                التصحيح»). Teachers see submissions from their own
                assessments; Admins / Academic_Supervisor see the full
                org-wide firehose. */}
            <Route
              path="/grading-queue"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Academic_Supervisor', 'Teacher']}>
                  <GradingQueuePage />
                </ProtectedRoute>
              }
            />

            {/* Assessment System — Phase P3 (Student Runner, Feb 2026).
                `/my-assessments` lists Active tests for the current
                student; `/take-assessment/:id` is the full-screen
                runner (timer + auto-save + submit). */}
            <Route
              path="/my-assessments"
              element={
                <ProtectedRoute allowedRoles={['Student']}>
                  <StudentAssessments />
                </ProtectedRoute>
              }
            />
            <Route
              path="/take-assessment/:assessmentId"
              element={
                <ProtectedRoute allowedRoles={['Student']}>
                  <AssessmentRunner />
                </ProtectedRoute>
              }
            />
            <Route
              path="/competition/:competitionId"
              element={
                <ProtectedRoute allowedRoles={['Student']}>
                  <CompetitionHub />
                </ProtectedRoute>
              }
            />
            <Route
              path="/competition/:competitionId/judge"
              element={
                <ProtectedRoute allowedRoles={['Teacher', 'Super_Admin', 'Academic_Supervisor']}>
                  <CompetitionJudgeConsole />
                </ProtectedRoute>
              }
            />
            {/* Aug 2026 — dual-judge collaborative question studio. */}
            <Route
              path="/competition/:competitionId/studio"
              element={
                <ProtectedRoute allowedRoles={['Teacher', 'Super_Admin', 'Academic_Supervisor']}>
                  <CompetitionStudio />
                </ProtectedRoute>
              }
            />
            {/* Aug 2026 — Competitions Phase 5: admin creation & management console. */}
            <Route
              path="/admin/competitions"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Academic_Supervisor']}>
                  <CompetitionsAdmin />
                </ProtectedRoute>
              }
            />

            <Route
              path="/change-password"
              element={
                <ProtectedRoute>
                  <ChangePassword />
                </ProtectedRoute>
              }
            />

            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />

            {/* Jun 29, 2026 — MED-01. `/profile` was previously an
                un-registered path that silently redirected to
                `/dashboard`. Bookmarks and shared links landed on the
                dashboard with no explanation. Alias to `/settings`
                (which already hosts the profile edit UI) so links keep
                working without introducing a new page. */}
            <Route path="/profile" element={<Navigate to="/settings" replace />} />
            <Route
              path="/audit-logs"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin']}>
                  <AuditLogs />
                </ProtectedRoute>
              }
            />

            <Route
              path="/leaderboard"
              element={
                <ProtectedRoute>
                  <Leaderboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/teacher/badges"
              element={
                <ProtectedRoute allowedRoles={['Teacher', 'Super_Admin', 'Academic_Supervisor']}>
                  <TeacherBadgesPage />
                </ProtectedRoute>
              }
            />

            {/* Phase 2 (Feb 2026) — Admin Shift Coverage matrix. */}
            <Route
              path="/admin/shifts"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Academic_Supervisor']}>
                  <AdminShiftCoveragePage />
                </ProtectedRoute>
              }
            />

            {/* Jun 2026 — Teacher Monthly Dues & Accountability (Super_Admin only). */}
            <Route
              path="/admin/teacher-dues"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin']}>
                  <TeacherDuesPage />
                </ProtectedRoute>
              }
            />

            {/* Aug 2026 — Dues review requests (financial transparency). */}
            <Route
              path="/admin/dues-reviews"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin']}>
                  <DuesReviewRequests />
                </ProtectedRoute>
              }
            />
            <Route
              path="/teacher/dues"
              element={
                <ProtectedRoute allowedRoles={['Teacher']}>
                  <TeacherDuesLogPage />
                </ProtectedRoute>
              }
            />

            {/* Phase 1 (Jun 2026) — Classroom Monitoring Center (Health Index). */}
            <Route
              path="/admin/classroom-monitor"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Academic_Supervisor']}>
                  <ClassroomMonitoringPage />
                </ProtectedRoute>
              }
            />

            {/* Aug 2026 — Platform Health merged into MESP CORE (redirect kept). */}
            <Route path="/admin/platform-health" element={<Navigate to="/admin/mesp-core" replace />} />

            <Route
              path="/admin/academic-assistant"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Academic_Supervisor']}>
                  <AcademicAssistant />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/health-monitor"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin']}>
                  <HealthMonitor />
                </ProtectedRoute>
              }
            />
            {/* Jun 2026 — App adoption dashboard (who installed the official app). */}
            <Route
              path="/admin/platform-adoption"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Academic_Supervisor']}>
                  <PlatformAdoption />
                </ProtectedRoute>
              }
            />
            {/* Aug 2026 — MESP CORE unified intelligence & action center (Super_Admin only). */}
            <Route
              path="/admin/mesp-core"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin']}>
                  <MespCore />
                </ProtectedRoute>
              }
            />

            {/* Jul 2026 — Duplicate student-profile merge engine (Super_Admin only). */}
            <Route
              path="/admin/duplicate-profiles"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin']}>
                  <DuplicateProfilesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/announcement"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin']}>
                  <AnnouncementManager />
                </ProtectedRoute>
              }
            />

            {/* Phase 3 (Jun 2026) — Teacher dedicated shift schedule page. */}
            <Route
              path="/teacher/shifts"
              element={
                <ProtectedRoute allowedRoles={['Teacher']}>
                  <TeacherShiftsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/teacher/leave"
              element={
                <ProtectedRoute allowedRoles={['Teacher']}>
                  <TeacherLeavePage />
                </ProtectedRoute>
              }
            />
            {/* Jun 2026 — Collaborative Teacher Enrichment Hub */}
            <Route
              path="/enrichment-hub"
              element={
                <ProtectedRoute allowedRoles={['Teacher', 'Super_Admin', 'Academic_Supervisor']}>
                  <EnrichmentHub />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/enrichment-monitor"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Academic_Supervisor']}>
                  <EnrichmentMonitor />
                </ProtectedRoute>
              }
            />
            {/* Jun 2026 — Lesson Architect (prepared lessons) */}
            <Route
              path="/lesson-architect"
              element={
                <ProtectedRoute allowedRoles={['Teacher']}>
                  <LessonArchitect />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/lesson-preps"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Academic_Supervisor']}>
                  <LessonPrepMonitor />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/academic-progress-monitor"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Academic_Supervisor']}>
                  <AcademicProgressTracker />
                </ProtectedRoute>
              }
            />

            {/* Phase 38 — Dedicated Student/Teacher badges page (isolated). */}
            <Route
              path="/badges"
              element={
                <ProtectedRoute allowedRoles={['Student', 'Teacher', 'Super_Admin', 'Academic_Supervisor']}>
                  <BadgesPage />
                </ProtectedRoute>
              }
            />

            {/* PROD AUDIT FIX (Feb 2026) — preserve old bookmarks / direct
                URLs that admins kept typing/sharing before the routes were
                consolidated. They used to silently land on /dashboard
                because of the catch-all below, which made it look like
                pages were broken. Redirect them to the canonical paths. */}
            <Route path="/registration-management" element={<Navigate to="/registration" replace />} />
            <Route path="/support-requests" element={<Navigate to="/support/inbox" replace />} />
            <Route path="/attendance-issues" element={<Navigate to="/work-queues?view=attendance-issues" replace />} />
            <Route path="/placement-test-results" element={<Navigate to="/placement-results" replace />} />
            {/* Jun 29, 2026 — MED-02. `/notifications` used to redirect
                to `/dashboard?openNotifications=1` — the query param
                polluted history + bookmarks, and no code read it. Use
                a hash instead: cleaner URL, keeps the intent of "land
                on dashboard with notifications drawer visible". */}
            <Route path="/notifications" element={<Navigate to="/dashboard#notifications" replace />} />

            {/* Phase 9 (Feb 2026, UX overhaul) — Admin Hub routes.
                Each Hub renders 4-6 existing pages as tabs inside a
                single landing surface. Permission gating is still done
                by the inner pages themselves; the outer ProtectedRoute
                only enforces the role membership. */}
            <Route
              path="/inbox"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Academic_Supervisor', 'Registration_Officer']} permission="can_manage_users">
                  <InboxHub />
                </ProtectedRoute>
              }
            />
            <Route
              path="/operations-hub"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Academic_Supervisor', 'Financial_Manager', 'Registration_Officer']} permission="can_access_students">
                  <OperationsHub />
                </ProtectedRoute>
              }
            />
            <Route
              path="/academic-hub"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Academic_Supervisor']} permission="can_access_classrooms">
                  <AcademicHub />
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance-hub"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Financial_Manager', 'Academic_Supervisor']} permission="can_access_finance">
                  <FinanceHub />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports-hub"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Academic_Supervisor', 'Financial_Manager']} permission="can_access_reports">
                  <ReportsHub />
                </ProtectedRoute>
              }
            />

            <Route
              path="/reports"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Academic_Supervisor', 'Teacher', 'Student', 'Parent']} permission="can_access_reports">
                  <Reports />
                </ProtectedRoute>
              }
            />

            <Route
              path="/reports/zoom-usage"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Academic_Supervisor']} permission="can_access_reports">
                  <ZoomUsageReport />
                </ProtectedRoute>
              }
            />

            <Route
              path="/zoom-requests"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Academic_Supervisor']} permission="can_access_classrooms">
                  <ZoomAccessRequests />
                </ProtectedRoute>
              }
            />

            <Route
              path="/placement-test"
              element={
                <ProtectedRoute allowedRoles={['Student']}>
                  <PlacementTest />
                </ProtectedRoute>
              }
            />

            <Route
              path="/placement-results"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Academic_Supervisor']} permission="can_access_distribution">
                  <PlacementTestResults />
                </ProtectedRoute>
              }
            />

            <Route
              path="/distribution"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Academic_Supervisor']} permission="can_access_distribution">
                  <Distribution />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/match-review"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Academic_Supervisor']} permission="can_access_distribution">
                  <MatchReview />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/placement-preview"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Academic_Supervisor']}>
                  <PlacementPreview />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/placement-v3-settings"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Academic_Supervisor']}>
                  <PlacementV3Settings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/pending-media"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin', 'Academic_Supervisor']}>
                  <PlacementV3PendingMedia />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/navigation"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin']}>
                  <NavigationEditor />
                </ProtectedRoute>
              }
            />
            {/* Board M5.C · public sandbox for the Kids/General theme
                preview iframe. NO auth required — the page contains
                no student data, only a visual sample. */}
            <Route
              path="/preview/placement-v3-theme"
              element={<PlacementV3ThemePreview />}
            />
            <Route
              path="/teacher-guide"
              element={
                <ProtectedRoute allowedRoles={['Teacher', 'Super_Admin', 'Academic_Supervisor']}>
                  <TeacherGuide />
                </ProtectedRoute>
              }
            />
            <Route
              path="/student-guide"
              element={
                <ProtectedRoute allowedRoles={['Student', 'Super_Admin', 'Academic_Supervisor', 'Parent']}>
                  <StudentGuide />
                </ProtectedRoute>
              }
            />
            
            {/* Default landing route (Feb 2026 — WelcomeLanding).
                Authenticated users are bounced to /dashboard by
                RootRoute; unauth visitors see the redesigned entry
                page with only 2 primary CTAs + Google. */}
            <Route path="/" element={<RootRoute />} />
            {/* Feb 2026 — Data Integrity & Operations Console. Super Admin only. */}
            <Route
              path="/admin/data-quality"
              element={
                <ProtectedRoute allowedRoles={['Super_Admin']}>
                  <DataQualityConsole />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          </Suspense>
          </ChatOverlayProvider>
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
